/**
 * Find a Dealer Block - Client-Side Filtering
 * Loads all dealers once, filters in browser for instant results
 */

import popupConfirm from '../../assets/scripts/common/popupConfirm.js';

(function () {
  'use strict';

  // Global state
  let allDealers = [];
  let filteredDealers = [];
  let map = null;
  let markers = [];
  let googleMapsLoaded = false;

  /**
   * Initialize the block
   */
  function init() {
    const block = document.querySelector('.find-a-dealer');
    if (!block) return;

    const config = {
      hideMap: block.dataset.hideMap === '1',
      usZipRadius: parseInt(block.dataset.usZipRadius || '50'),
      currentLocationRadius: parseInt(block.dataset.currentLocationRadius || '50'),
      googleMapsApiKey: block.dataset.googleMapsApiKey || ''
    };

    const dealersList = block.querySelector('#dealers-list');
    const resultsCount = block.querySelector('.find-a-dealer__results-count'); // Optional element
    const mapContainer = block.querySelector('#dealers-map');

    if (!dealersList) {
      console.error('Find a Dealer: Required DOM elements not found');
      return;
    }

    loadAllDealers(config).then(() => {
      setupSearchHandlers(config, dealersList, resultsCount);

      if (!config.hideMap && mapContainer && config.googleMapsApiKey) {
        loadGoogleMapsScript(config.googleMapsApiKey).then(() => {
          initializeMap(mapContainer, config, dealersList, resultsCount);
        }).catch(error => {
          console.error('Find a Dealer: Failed to load Google Maps', error);
        });
      }

      showEmptyState(dealersList, resultsCount);
    }).catch(error => {
      console.error('Find a Dealer: Failed to load dealers', error);
      showErrorPopup('Failed to Load Dealers', 'Unable to load dealer information. Please refresh the page and try again.');
      showError(dealersList, 'Failed to load dealers. Please refresh the page.');
    });
  }

  /**
   * Load all dealers from the cached endpoint
   */
  async function loadAllDealers(config) {
    const url = '/wp-json/dealers/v1/all';

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

    const data = await response.json();
      allDealers = data.dealers || [];

      if (data.metadata && data.metadata.countries) {
        populateCountryDropdown(data.metadata.countries);
      }

      return allDealers;
    } catch (error) {
      console.error('Find a Dealer: Error loading dealers', error);
      throw error;
    }
  }

  /**
   * Populate country dropdown with loaded countries
   */
  function populateCountryDropdown(countries) {
    const select = document.getElementById('country-select');
    if (!select) return;

    const placeholder = select.querySelector('option[value=""]');
    select.innerHTML = '';
    if (placeholder) {
      select.appendChild(placeholder);
    }

    countries.forEach(country => {
      const option = document.createElement('option');
      option.value = country;
      option.textContent = country;
      select.appendChild(option);
    });
  }

  /**
   * Setup search input event handlers
   */
  function setupSearchHandlers(config, dealersList, resultsCount) {
    let zipDebounceTimer = null;

    // ZIP code input with debounce
    const zipInput = document.getElementById('us-zipcode');
    if (zipInput) {
      zipInput.addEventListener('input', (e) => {
        const zipcode = e.target.value.trim();

        // Clear previous timer
        clearTimeout(zipDebounceTimer);

        // Clear results if input is empty
        if (!zipcode) {
          showEmptyState(dealersList, resultsCount);
          return;
        }

        // Wait 500ms after user stops typing
        zipDebounceTimer = setTimeout(() => {
          if (validateZipCode(zipcode)) {
            filterByZipCode(zipcode, config.usZipRadius, dealersList, resultsCount);
          } else if (zipcode.length >= 5) {
            showErrorPopup('Invalid ZIP Code', 'Please enter a valid US ZIP code (5 digits).');
            showError(dealersList, 'Please enter a valid US ZIP code (5 digits)');
          }
        }, 500);
      });
    }

    // Country dropdown - immediate search on change
    const countrySelect = document.getElementById('country-select');
    if (countrySelect) {
      countrySelect.addEventListener('change', (e) => {
        const country = e.target.value;

        // Clear ZIP input
        if (zipInput) {
          zipInput.value = '';
        }

        if (!country) {
          showEmptyState(dealersList, resultsCount);
          return;
        }

        filterByCountry(country, dealersList, resultsCount);
      });
    }

    // Current location button
    const locationButton = document.querySelector('[data-filter-type="location"]');
    if (locationButton) {
      locationButton.addEventListener('click', () => {
        filterByCurrentLocation(config.currentLocationRadius, dealersList, resultsCount);
      });
    }
  }

  /**
   * Filter dealers by ZIP code (geocode ZIP, then find nearest)
   */
  async function filterByZipCode(zipcode, radius, dealersList, resultsCount) {
    toggleLoader(true);

    // Clear country select
    const countrySelect = document.getElementById('country-select');
    if (countrySelect) {
      countrySelect.value = '';
    }

    try {
      const exactMatches = allDealers.filter(d => d.zipcode === zipcode);
      if (exactMatches.length > 0) {
        filteredDealers = exactMatches;
        displayResults(filteredDealers, dealersList, resultsCount);
        updateMapMarkers(filteredDealers);
  toggleLoader(false);
        return;
      }

      const coords = await geocodeZipCode(zipcode);

      const dealersWithDistance = allDealers
        .filter(d => d.location.lat && d.location.lng)
        .map(dealer => {
          const distance = calculateDistance(
            coords.lat,
            coords.lng,
            parseFloat(dealer.location.lat),
            parseFloat(dealer.location.lng)
          );
          return { ...dealer, distance };
        })
        .filter(d => d.distance <= radius)
        .sort((a, b) => a.distance - b.distance);

      if (dealersWithDistance.length === 0) {
        const allWithDistance = allDealers
          .filter(d => d.location.lat && d.location.lng)
          .map(dealer => {
            const distance = calculateDistance(
              coords.lat,
              coords.lng,
              parseFloat(dealer.location.lat),
              parseFloat(dealer.location.lng)
            );
            return { ...dealer, distance };
          })
          .sort((a, b) => a.distance - b.distance);

        filteredDealers = allWithDistance.slice(0, 1);
      } else {
        filteredDealers = dealersWithDistance;
      }

      displayResults(filteredDealers, dealersList, resultsCount);
      updateMapMarkers(filteredDealers);
    } catch (error) {
      console.error('Find a Dealer: ZIP code search error', error);
      showErrorPopup('Unable to search by ZIP code', 'Please check the ZIP code and try again.');
      showError(dealersList, 'Unable to search by ZIP code. Please try again.');
    } finally {
      toggleLoader(false);
    }
  }

  /**
   * Filter dealers by country
   */
  function filterByCountry(country, dealersList, resultsCount) {
    toggleLoader(true);
    filteredDealers = allDealers.filter(d => d.country === country);
    displayResults(filteredDealers, dealersList, resultsCount);
    updateMapMarkers(filteredDealers);
    toggleLoader(false);
  }

  /**
   * Filter dealers by current location
   */
  async function filterByCurrentLocation(radius, dealersList, resultsCount) {
    toggleLoader(true);

    try {
      const position = await getCurrentLocation();
      filterByLocation(position.latitude, position.longitude, radius, dealersList, resultsCount);
    } catch (error) {
      console.error('Find a Dealer: Geolocation error', error);
      showErrorPopup('Location Error', error.message);
      showError(dealersList, error.message);
      toggleLoader(false);
    }
  }

  /**
   * Filter dealers by specific location (lat/lng)
   */
  function filterByLocation(lat, lng, radius, dealersList, resultsCount) {
    // Clear country select and ZIP input
    const countrySelect = document.getElementById('country-select');
    if (countrySelect) {
      countrySelect.value = '';
    }
    const zipInput = document.getElementById('us-zipcode');
    if (zipInput) {
      zipInput.value = '';
    }

    const dealersWithDistance = allDealers
      .filter(d => d.location.lat && d.location.lng)
      .map(dealer => {
        const distance = calculateDistance(
          lat,
          lng,
          parseFloat(dealer.location.lat),
          parseFloat(dealer.location.lng)
        );
        return { ...dealer, distance };
      })
      .filter(d => d.distance <= radius)
      .sort((a, b) => a.distance - b.distance);

    if (dealersWithDistance.length === 0) {
      const allWithDistance = allDealers
        .filter(d => d.location.lat && d.location.lng)
        .map(dealer => {
          const distance = calculateDistance(
            lat,
            lng,
            parseFloat(dealer.location.lat),
            parseFloat(dealer.location.lng)
          );
          return { ...dealer, distance };
        })
        .sort((a, b) => a.distance - b.distance);

      filteredDealers = allWithDistance.slice(0, 1);
      } else {
      filteredDealers = dealersWithDistance;
    }

    displayResults(filteredDealers, dealersList, resultsCount);
    updateMapMarkers(filteredDealers);
    toggleLoader(false);
  }

  /**
   * Calculate distance between two lat/lng points using Haversine formula
   * Returns distance in miles
   */
  function calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  /**
   * Geocode US ZIP code using Google Geocoding API
   */
  async function geocodeZipCode(zipcode) {
    const apiKey = document.querySelector('.find-a-dealer').dataset.googleMapsApiKey;
    if (!apiKey) {
      throw new Error('Google Maps API key not configured');
    }

    const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${zipcode},USA&key=${apiKey}`;
    const response = await fetch(url);
    const data = await response.json();

    if (data.status === 'OK' && data.results.length > 0) {
      return {
        lat: data.results[0].geometry.location.lat,
        lng: data.results[0].geometry.location.lng
      };
    } else {
      throw new Error('Unable to geocode ZIP code');
    }
  }

  /**
   * Get user's current location
   */
  function getCurrentLocation() {
    return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
      return;
    }

      const isSecure = window.location.protocol === 'https:';
      const isLocalhost = window.location.hostname === 'localhost' ||
                          window.location.hostname === '127.0.0.1' ||
                          window.location.hostname.includes('.test') ||
                          window.location.hostname.startsWith('192.168.');

      if (!isSecure && !isLocalhost) {
        reject(new Error('Geolocation requires a secure connection (HTTPS).'));
        return;
      }

    navigator.geolocation.getCurrentPosition(
      (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          let message = 'Unable to get your current location. ';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              message += 'Please enable location permissions for this site in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              message += 'Location information is unavailable. Please check your device settings.';
              break;
            case error.TIMEOUT:
              message += 'Location request timed out. Please try again.';
              break;
            default:
              message += 'An unknown error occurred.';
          }

          reject(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
      }
    );
  });
  }

  /**
   * Display filtered results
   */
  function displayResults(dealers, dealersList, resultsCount) {
    if (resultsCount) {
      resultsCount.textContent = `${dealers.length} dealer${dealers.length !== 1 ? 's' : ''} found`;
    }
    dealersList.innerHTML = '';

    if (dealers.length === 0) {
      dealersList.innerHTML = '<p class="find-a-dealer__no-results">No dealers found. Please try a different search.</p>';
      return;
    }

    dealers.forEach(dealer => {
      const card = createDealerCard(dealer);
      dealersList.appendChild(card);
    });
  }

  /**
   * Create dealer card HTML following dealer-card.php template structure
   */
  function createDealerCard(dealer) {
    const card = document.createElement('div');
    card.className = 'dealer-card';
    card.setAttribute('data-dealer-id', dealer.id);
    card.setAttribute('data-lat', dealer.location.lat || '');
    card.setAttribute('data-lng', dealer.location.lng || '');

    // Build card HTML following template structure
    let html = '<div class="dealer-card__info">';

    // Title
    html += `<h3 class="h4">${escapeHtml(dealer.title)}</h3>`;

    // Distance (if available)
    if (dealer.distance !== undefined && dealer.distance !== null) {
      html += `<div class="dealer-card__distance">${dealer.distance.toFixed(1)} miles away</div>`;
    }

    // Country
    if (dealer.country) {
      html += `${escapeHtml(dealer.country)}<br/>`;
    }

    // Street address - check both nested and root level
    const street = dealer.address?.street || dealer.street_address;
    if (street) {
      html += `${escapeHtml(street)}<br/>`;
    }

    // City, State, Zipcode - check both nested and root level
    const city = dealer.address?.city || dealer.city;
    const state = dealer.address?.state || dealer.state;
    const zipcode = dealer.address?.zipcode || dealer.zipcode;

    const cityStateLine = [city, state, zipcode].filter(Boolean).join(', ');
    if (cityStateLine) {
      html += `${escapeHtml(cityStateLine)}<br/>`;
    }

    // Country from address (if different from main country)
    const addressCountry = dealer.address?.country;
    if (addressCountry && addressCountry !== dealer.country) {
      html += `${escapeHtml(addressCountry)}<br/>`;
    }

    // Content/Description
    if (dealer.content) {
      html += `<div class="dealer-card__description">${dealer.content}</div>`;
    }

    // Actions/Contact info
    html += '<div class="actions">';

    if (dealer.phone) {
      html += `<div class="type-light-grey-black"><a href="tel:${escapeHtml(dealer.phone)}">P: ${escapeHtml(dealer.phone)}</a></div>`;
    }

    if (dealer.fax) {
      html += `<div class="type-light-grey-black"><span>Fax: ${escapeHtml(dealer.fax)}</span></div>`;
    }

    if (dealer.email) {
      html += `<div class="type-light-grey-black"><a href="mailto:${escapeHtml(dealer.email)}">${escapeHtml(dealer.email)}</a></div>`;
    }

    if (dealer.website) {
      html += `<div class="type-light-grey-black"><a href="${escapeHtml(dealer.website)}" target="_blank" rel="noopener noreferrer">${escapeHtml(dealer.website)}</a></div>`;
    }

    html += '</div>'; // Close actions
    html += '</div>'; // Close dealer-card__info

    // Map button
    if (dealer.location.lat && dealer.location.lng) {
      html += '<div class="dealer-card__actions actions">';
      html += '<div class="type-sport">';
      html += `<button class="dealer-card__map-btn" data-dealer-id="${dealer.id}">View on Map</button>`;
      html += '</div>';
      html += '</div>';
    }

    card.innerHTML = html;

    // Attach map button click handler
    const mapBtn = card.querySelector('.dealer-card__map-btn');
    if (mapBtn && map) {
      mapBtn.addEventListener('click', () => {
        map.setCenter({
          lat: parseFloat(dealer.location.lat),
          lng: parseFloat(dealer.location.lng)
        });
        map.setZoom(15);
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }

    return card;
  }


  /**
   * Create custom marker content element
   */
  function createMarkerContent(config) {
    const content = document.createElement('div');
    content.style.width = '32px';
    content.style.height = '32px';

    if (config.pinIconUrl) {
      const img = document.createElement('img');
      img.src = config.pinIconUrl;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';
      content.appendChild(img);
    }

    return content;
  }

  /**
   * Update map markers with filtered dealers
   */
  function updateMapMarkers(dealers) {
    if (!map) return;

    markers.forEach(marker => marker.map = null);
    markers = [];

    const dealersWithCoords = dealers.filter(d => d.location.lat && d.location.lng);
    if (dealersWithCoords.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    const config = { pinIconUrl: '/app/themes/garrett-unified/assets/images/pin.png' };

    dealersWithCoords.forEach(dealer => {
      const position = {
        lat: parseFloat(dealer.location.lat),
        lng: parseFloat(dealer.location.lng)
      };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: position,
        map: map,
        content: createMarkerContent(config),
        title: dealer.title
      });

      marker.addListener('click', () => {
        const card = document.querySelector(`[data-dealer-id="${dealer.id}"]`);
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          card.classList.add('highlight');
          setTimeout(() => card.classList.remove('highlight'), 2000);
        }
      });

      markers.push(marker);
      bounds.extend(position);
    });

    map.fitBounds(bounds);
    const padding = { top: 50, right: 50, bottom: 50, left: 50 };
    map.fitBounds(bounds, padding);

    if (markers.length === 1) {
      google.maps.event.addListenerOnce(map, 'idle', () => {
        const currentZoom = map.getZoom();
        if (currentZoom > 15) {
          map.setZoom(15);
        }
      });
    }
  }

  /**
   * Initialize Google Map
   */
  function initializeMap(container, config, dealersList, resultsCount) {
    map = new google.maps.Map(container, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      mapId: 'DEALER_MAP'
    });

    loadInitialMapPins(config, dealersList, resultsCount);
  }

  /**
   * Load initial aggregated map pins (one per state/country)
   */
  async function loadInitialMapPins(config, dealersList, resultsCount) {
    try {
      const response = await fetch('/wp-json/dealers/v1/map-data');
      const data = await response.json();

      if (data.pins && data.pins.length > 0) {
        displayAggregatedPins(data.pins, dealersList, resultsCount);
      }
    } catch (error) {
      console.error('Find a Dealer: Failed to load map pins', error);
    }
  }

  /**
   * Display aggregated pins on map
   */
  function displayAggregatedPins(pins, dealersList, resultsCount) {
    markers.forEach(marker => marker.map = null);
    markers = [];

    const bounds = new google.maps.LatLngBounds();
    const config = { pinIconUrl: '/app/themes/garrett-unified/assets/images/pin.png' };

    pins.forEach(pin => {
      const position = { lat: parseFloat(pin.lat), lng: parseFloat(pin.lng) };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position: position,
        map: map,
        content: createMarkerContent(config),
        title: `${pin.name} (${pin.count} dealers)`
      });

      marker.addListener('click', () => {
        if (pin.type === 'state') {
          filterByState(pin.filter_value, dealersList, resultsCount);
        } else if (pin.type === 'country') {
          filterByCountry(pin.filter_value, dealersList, resultsCount);
        }
      });

      markers.push(marker);
      bounds.extend(position);
    });

    if (markers.length > 0) {
      map.fitBounds(bounds);
      const padding = { top: 50, right: 50, bottom: 50, left: 50 };
      map.fitBounds(bounds, padding);
    }
  }

  /**
   * Filter dealers by state
   */
  function filterByState(state, dealersList, resultsCount) {
    toggleLoader(true);
    filteredDealers = allDealers.filter(d => d.state === state);
    displayResults(filteredDealers, dealersList, resultsCount);
    updateMapMarkers(filteredDealers);
    toggleLoader(false);
  }

  /**
   * Load Google Maps API script
   */
  function loadGoogleMapsScript(apiKey) {
    return new Promise((resolve, reject) => {
      if (googleMapsLoaded || window.google?.maps) {
        googleMapsLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=marker&callback=initGoogleMaps&v=weekly`;
      script.async = true;
      script.defer = true;

      // Create global callback
      window.initGoogleMaps = () => {
        googleMapsLoaded = true;
        delete window.initGoogleMaps;
        resolve();
      };

      script.onerror = () => {
        delete window.initGoogleMaps;
        reject(new Error('Failed to load Google Maps'));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Validate US ZIP code format
   */
  function validateZipCode(zipcode) {
    return /^\d{5}$/.test(zipcode);
  }

  /**
   * Show empty state
   */
  function showEmptyState(dealersList, resultsCount) {
    if (resultsCount) {
      resultsCount.textContent = '0 dealers found';
    }
    dealersList.innerHTML = '<p class="h5">Use the filters above to find dealers near you.</p>';
  }

  /**
   * Show error popup to user
   */
  function showErrorPopup(title, message) {
    popupConfirm(
      title,
      message,
      '',
      'OK',
      '' // No cancel button
    );
  }

  /**
   * Show error message
   */
  function showError(dealersList, message) {
    dealersList.innerHTML = `<p class="find-a-dealer__error">${escapeHtml(message)}</p>`;
  }

  /**
   * Toggle loading state
   */
  function toggleLoader(show) {
    const loader = document.querySelector('.find-a-dealer__loader');
    if (loader) {
      loader.style.display = show ? 'block' : 'none';
    }
  }

  /**
   * Decode HTML entities
   */
  function decodeHtmlEntities(text) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Escape HTML
   */
  function escapeHtml(text) {
    const decodedText = decodeHtmlEntities(text);
    const div = document.createElement('div');
    div.textContent = decodedText;
    return div.innerHTML;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
